import { useQuery } from '@tanstack/react-query'
import { fetcher } from 'repositories/BerthApi/berthApiClient'
import { getBerthManagementsEndpoint } from 'repositories/BerthApi/getBerthManagements/endpoint'
import type { GetBerthManagementsResponse } from 'repositories/BerthApi/getBerthManagements/type'
import type { Booking } from 'repositories/BerthApi/types/booking'
import type { BookingMasterData } from 'repositories/BerthApi/getBookingMasterData/type'
import { berthKeys } from 'repositories/queryKeys'

import { clipBookingToDate, getBookingKey, toCard } from '../utils/berthManagementHelpers'

const REFETCH_INTERVAL = 15000

export function useBerthManagements(
  tenantId: string | null,
  warehouseId: string | null,
  berthId: string | null,
  date: string,
  masterData: BookingMasterData | null
) {
  const bookingDate = date.replace(/-/g, '')

  const key =
    tenantId && warehouseId
      ? getBerthManagementsEndpoint(
          tenantId,
          warehouseId,
          bookingDate,
          berthId ? berthId : undefined
        )
      : null

  const { data, error, isLoading } = useQuery<GetBerthManagementsResponse>({
    queryKey: berthKeys.management(tenantId ?? '', warehouseId ?? '', bookingDate, berthId ?? ''),
    queryFn: () => fetcher(key!),
    enabled: !!key,
    refetchInterval: REFETCH_INTERVAL,
    refetchIntervalInBackground: false,
  })

  const berthData = data?.data.berths?.find((b) => b.berthId === berthId)
  const bookings = berthData?.bookings ?? []
  const allCards = bookings.map((b) => ({
    ...toCard(clipBookingToDate(b, bookingDate), masterData),
    originalStartTime: b.startTime ?? undefined,
    originalEndTime: b.endTime ?? undefined,
  }))
  const assignedCards = allCards.filter((c) => c.assigned)
  const unassignedCards = allCards.filter((c) => !c.assigned)

  // 生の Booking データへの ID→Booking マップ（詳細モーダル用）
  const bookingsMap: Record<string, Booking> = {}
  for (const b of bookings) {
    bookingsMap[getBookingKey(b)] = b
  }

  return {
    berthData,
    assignedCards,
    unassignedCards,
    bookingsMap,
    isLoading,
    error,
  }
}
